import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { VACANCY_STATUS } from './dashboard.proxy';
import { DashboardActions } from './index';

export const dashboardFeatureKey = 'dashboard';
export interface DashboardState {
  staticData: any;
  staticDataLoadFailed: boolean;
  vacancyList: any;
  vacancyListCounts: any;
  vacancyListPage: number;
  vacancyListLoading: boolean;
  matchesList: any;
  matchDetails: any;
  vacancyDetails: any;
  talentDetails: any;
  calculatingMatch: boolean;
  globalMatchCount: null | { handledCount: number, totalCount: number }
}

const initialState: DashboardState = {
  staticData: null,
  staticDataLoadFailed: false,
  vacancyList: [],
  vacancyListCounts: null,
  vacancyListPage: 0,
  vacancyListLoading: true,
  matchesList: [],
  matchDetails: null,
  vacancyDetails: null,
  talentDetails: null,
  calculatingMatch: false,
  globalMatchCount: null
};

const reducer = createReducer<DashboardState>(
  initialState,
  on(DashboardActions.GetStaticData, (state) => ({ ...state, staticDataLoadFailed: false })),
  on(DashboardActions.GetStaticDataSuccess, (state, action) => ({ ...state, staticData: action.payload })),
  on(DashboardActions.GetStaticDataFailure, (state) => ({ ...state, staticDataLoadFailed: true })),
  on(DashboardActions.HmVacanciesLoading, (state, action) => ({ ...state, vacancyListLoading: action.loading })),
  on(DashboardActions.GetHmVacanciesSuccess, (state, action) => ({ 
    ...state,
    vacancyList: action.toPush ? [ ...state.vacancyList, ...action.payload.vacancies ] : action.payload.vacancies,
    vacancyListCounts: action.payload.counters,
    vacancyListPage: action.loadMore ? action.toPush ? state.vacancyListPage + 1 : 0 : null
  })),
  on(DashboardActions.ClearHmVacancies, (state, action) => ({ 
    ...state,
    vacancyList: [],
    vacancyListCounts: null,
    vacancyListPage: 0
  })),
  on(DashboardActions.SetVacancyStatus, (state, action) => {
    return {
      ...state, 
      vacancyList: action.status === VACANCY_STATUS.deleted 
        ? state.vacancyList.filter(vac => vac.id !== action.id)
        : state.vacancyList.map(vac => vac.id === action.id 
          ? { ...vac, status: action.status, published: new Date().toISOString() } 
          : vac
        )
    }
  }),
  on(DashboardActions.GetMatchByIdSuccess, (state, action) => ({ ...state, matchDetails: action.payload })),
  on(DashboardActions.GetVacancyByIdSuccess, (state, action) => ({ ...state, vacancyDetails: action.payload })),
  on(DashboardActions.DropVacancyDetails, (state, action) => ({ ...state, vacancyDetails: null })),
  on(DashboardActions.GetTalentByIdSuccess, (state, action) => ({ ...state, talentDetails: action.payload })),
  on(DashboardActions.DropTalentDetails, (state, action) => ({ ...state, talentDetails: null })),
  on(DashboardActions.StartMatchCalculation, (state, action) => ({ ...state, calculatingMatch: true })),
  on(DashboardActions.EndMatchCalculation, (state, action) => ({ ...state, calculatingMatch: false })),
  on(DashboardActions.SetGlobalMatchCount, (state, action) => ({ ...state, globalMatchCount: action.payload })),
  on(DashboardActions.AdjustHardSoftSkills, (state, action) => ({ 
    ...state, 
    vacancyDetails: { 
      ...state.vacancyDetails, 
      hardSkills: action.hardSkills ? action.hardSkills : state.vacancyDetails.hardSkills, 
      softSkills: action.softSkills ? action.softSkills : state.vacancyDetails.softSkills
    }
  }))
);

export function dashboardReducer(state: DashboardState | undefined, action: Action) {
  return reducer(state, action);
}

export const selectDashboardState = createFeatureSelector<DashboardState>(dashboardFeatureKey);

export const selectStatic = (type: string) => createSelector(
  selectDashboardState,
  (state) => {
    if (state.staticDataLoadFailed) {
      return [];
    }

    return state.staticData ? state.staticData[type] || [] : null;
  }
);

export const checkStaticData = createSelector(
  selectDashboardState,
  (state) => !!state.staticData
);

export const selectHmVacancyListLoading = createSelector(
  selectDashboardState,
  (state) => state.vacancyListLoading
);

export const selectHmVacancyListCounts = createSelector(
  selectDashboardState,
  (state) => state.vacancyListCounts
);

export const selectHmVacancyList = createSelector(
  selectDashboardState,
  (state) => state.vacancyList
);

export const selectHmVacancyListPage = createSelector(
  selectDashboardState,
  (state) => state.vacancyListPage
);

export const selectMatchDetails = createSelector(
  selectDashboardState,
  (state) => state.matchDetails
);

export const selectVacancyDetails = createSelector(
  selectDashboardState,
  (state) => state.vacancyDetails
);

export const selectTalentDetails = createSelector(
  selectDashboardState,
  (state) => state.talentDetails
);

export const selectMatchCalculation = createSelector(
  selectDashboardState,
  (state) => state.calculatingMatch
);

export const selectGloabalMatchCount = createSelector(
  selectDashboardState,
  (state) => state.globalMatchCount
);
